import React, { useState } from 'react'
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material'
import { Pool } from '@mui/icons-material'
import { Auth } from '@supabase/auth-ui-react'
import { ThemeSupa } from '@supabase/auth-ui-shared'
import { supabase } from '../../services/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { Navigate } from 'react-router-dom'
import usePageTracking from '../../hooks/usePageTracking'

const Login = () => {
  usePageTracking('Login');
  
  const { user } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [view, setView] = useState('sign_in')

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #f0f9ff 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
          <Avatar
            sx={{
              width: { xs: 56, md: 64 },
              height: { xs: 56, md: 64 },
              bgcolor: 'primary.main',
              mx: 'auto',
              mb: 2,
            }}
          >
            <Pool sx={{ fontSize: { xs: 28, md: 32 } }} />
          </Avatar>
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700, color: 'text.primary' }}
          >
            {view === 'sign_in' ? 'Sign In to TFPLog' : 'Create Your TFPLog Account'}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}
          >
            {view === 'sign_in' 
              ? 'Access your pool chemistry tracking dashboard'
              : 'Start tracking your pool chemistry today'
            }
          </Typography>
        </Box>

        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: { xs: 2, md: 3 },
            maxWidth: '100%',
            width: '100%',
          }}
        >
          <Box sx={{ mb: 3, pb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                textAlign: 'center'
              }}
            >
              {view === 'sign_in' ? '🔐 Sign In' : '✨ Create New Account'}
            </Typography>
          </Box>
          <Box sx={{
            '& .supabase-auth-ui_ui-anchor': {
              display: 'none !important',
            },
          }}>
          <Auth
            supabaseClient={supabase}
            view={view}
            appearance={{
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: theme.palette.primary.main,
                    brandAccent: theme.palette.primary.dark,
                    brandButtonText: 'white',
                    defaultButtonBackground: theme.palette.background.default,
                    defaultButtonBackgroundHover: theme.palette.action.hover,
                    defaultButtonBorder: theme.palette.divider,
                    defaultButtonText: theme.palette.text.primary,
                    dividerBackground: theme.palette.divider,
                    inputBackground: theme.palette.background.paper,
                    inputBorder: theme.palette.divider,
                    inputBorderHover: theme.palette.action.hover,
                    inputBorderFocus: theme.palette.primary.main,
                    inputText: theme.palette.text.primary,
                    inputLabelText: theme.palette.text.secondary,
                    inputPlaceholder: theme.palette.text.disabled,
                    messageText: theme.palette.error.main,
                    messageTextDanger: theme.palette.error.main,
                    anchorTextColor: theme.palette.primary.main,
                    anchorTextHoverColor: theme.palette.primary.dark,
                  },
                  space: {
                    spaceSmall: '4px',
                    spaceMedium: '8px',
                    spaceLarge: '16px',
                    labelBottomMargin: '8px',
                    anchorBottomMargin: '4px',
                    emailInputSpacing: '4px',
                    socialAuthSpacing: '4px',
                    buttonPadding: isMobile ? '12px 16px' : '14px 20px',
                    inputPadding: isMobile ? '12px 16px' : '14px 16px',
                  },
                  fontSizes: {
                    baseBodySize: isMobile ? '14px' : '16px',
                    baseInputSize: isMobile ? '16px' : '16px',
                    baseLabelSize: isMobile ? '14px' : '14px',
                    baseButtonSize: isMobile ? '16px' : '16px',
                  },
                  fonts: {
                    bodyFontFamily: theme.typography.fontFamily,
                    buttonFontFamily: theme.typography.fontFamily,
                    inputFontFamily: theme.typography.fontFamily,
                    labelFontFamily: theme.typography.fontFamily,
                  },
                  borderWidths: {
                    buttonBorderWidth: '1px',
                    inputBorderWidth: '1px',
                  },
                  radii: {
                    borderRadiusButton: '8px',
                    buttonBorderRadius: '8px',
                    inputBorderRadius: '8px',
                  },
                },
              },
            }}
            providers={['google']}
            redirectTo={`${window.location.origin}/dashboard`}
            showLinks={true}
            localization={{
              variables: {
                sign_in: {
                  email_label: 'Email address',
                  password_label: 'Password',
                  email_input_placeholder: 'Enter your email address',
                  password_input_placeholder: 'Enter your password',
                  button_label: 'Sign in',
                  loading_button_label: 'Signing in...',
                  social_provider_text: 'Sign in with {{provider}}',
                },
                sign_up: {
                  email_label: 'Email address',
                  password_label: 'Create a password',
                  email_input_placeholder: 'Enter your email address',
                  password_input_placeholder: 'Create a strong password',
                  button_label: 'Create account',
                  loading_button_label: 'Creating account...',
                  social_provider_text: 'Sign up with {{provider}}',
                },
              },
            }}
          />
          </Box>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography
              variant="body2"
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setView(view === 'sign_in' ? 'sign_up' : 'sign_in')
              }}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {view === 'sign_in' 
                ? "Don't have an account? Sign up"
                : "Already have an account? Sign in"
              }
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ textAlign: 'center', mt: { xs: 3, md: 4 } }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
          >
            Secure authentication powered by Supabase
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default Login